/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    p: "p",
    a: "a",
    code: "code",
    pre: "pre",
    h3: "h3",
    strong: "strong"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "附加额外属性(Attaching additional props)"
    }), "\n", _jsx(_components.p, {
      children: "¥Attaching additional props"
    }), "\n", _jsx(_components.p, {
      children: "| v2"
    }), "\n", _jsxs(_components.p, {
      children: ["为了避免不必要的封装器仅将一些属性传递给渲染的组件或元素，你可以使用 ", _jsxs(_components.a, {
        href: "/docs/api#attrs",
        children: [_jsx(_components.code, {
          children: ".attrs"
        }), " 构造函数"]
      }), "。它允许你将附加属性（或 \"attributes\"）附加到组件。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥To avoid unnecessary wrappers that just pass on some props to the rendered component or element, you can use the ", _jsxs(_components.a, {
        href: "/docs/api#attrs",
        children: [_jsx(_components.code, {
          children: ".attrs"
        }), " constructor"]
      }), ". It allows you to attach additional props (or \"attributes\") to a component."]
    }), "\n", _jsxs(_components.p, {
      children: ["通过这种方式，你可以将静态属性附加到元素或将第三方属性（如 ", _jsx(_components.code, {
        children: "activeClassName"
      }), "）传递给 React Router 的 Link 组件。此外，你还可以将更多动态属性附加到组件。", _jsx(_components.code, {
        children: ".attrs"
      }), " 对象还采用函数来接收组件接收的 props。返回值也将合并到生成的 props 中。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥This way you can for example attach static props to an element or pass a third-party prop like ", _jsx(_components.code, {
        children: "activeClassName"
      }), " to React Router's Link component. Furthermore, you can also attach more dynamic props to a component. The ", _jsx(_components.code, {
        children: ".attrs"
      }), " object also takes functions, that receive the props that the component receives. The return value will be merged into the resulting props as well."]
    }), "\n", _jsxs(_components.p, {
      children: ["这里我们渲染一个 ", _jsx(_components.code, {
        children: "Input"
      }), " 组件并为其附加一些动态和静态属性："]
    }), "\n", _jsxs(_components.p, {
      children: ["¥Here we render an ", _jsx(_components.code, {
        children: "Input"
      }), " component and attach some dynamic and static attributes to it:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-react",
        children: "const Input = styled.input.attrs<{ $size?: string; }>(props => ({\n  // we can define static props\n  type: \"text\",\n\n  // or we can define dynamic ones\n  $size: props.$size || \"1em\",\n}))`\n  color: #BF4F74;\n  font-size: 1em;\n  border: 2px solid #BF4F74;\n  border-radius: 3px;\n\n  /* here we use the dynamically computed prop */\n  margin: ${props => props.$size};\n  padding: ${props => props.$size};\n`;\n\nrender(\n  <div>\n    <Input placeholder=\"A small text input\" />\n    <br />\n    <Input placeholder=\"A bigger text input\" $size=\"2em\" />\n  </div>\n);\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["正如你所看到的，我们可以在插值中访问新创建的 props，并且 ", _jsx(_components.code, {
        children: "type"
      }), " 属性会向下传递给元素。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥As you can see, we get access to our newly created props in the interpolations, and the ", _jsx(_components.code, {
        children: "type"
      }), " attribute is passed down to the element."]
    }), "\n", _jsx(_components.h3, {
      children: "覆盖 .attrs(Overriding .attrs)"
    }), "\n", _jsx(_components.p, {
      children: "¥Overriding .attrs"
    }), "\n", _jsxs(_components.p, {
      children: ["请注意，在封装样式组件时，", _jsx(_components.code, {
        children: ".attrs"
      }), " 将从最里面的样式组件应用到最外面的样式组件。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥Notice that when wrapping styled components, ", _jsx(_components.code, {
        children: ".attrs"
      }), " are applied from the innermost styled component to the outermost styled component."]
    }), "\n", _jsxs(_components.p, {
      children: ["这允许每个封装器覆盖 ", _jsx(_components.code, {
        children: ".attrs"
      }), " 的嵌套使用，类似于样式表中稍后定义的 CSS 属性如何覆盖先前的声明。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥This allows each wrapper to ", _jsx(_components.strong, {
        children: "override"
      }), " nested uses of ", _jsx(_components.code, {
        children: ".attrs"
      }), ", similarly to how CSS properties defined later in a stylesheet override previous declarations."]
    }), "\n", _jsxs(_components.p, {
      children: ["首先应用 ", _jsx(_components.code, {
        children: "Input"
      }), " 的 ", _jsx(_components.code, {
        children: ".attrs"
      }), "，然后应用 ", _jsx(_components.code, {
        children: "PasswordInput"
      }), " 的 ", _jsx(_components.code, {
        children: ".attrs"
      }), "："]
    }), "\n", _jsxs(_components.p, {
      children: ["¥", _jsx(_components.code, {
        children: "Input"
      }), "'s ", _jsx(_components.code, {
        children: ".attrs"
      }), " are applied first, and then ", _jsx(_components.code, {
        children: "PasswordInput"
      }), "'s ", _jsx(_components.code, {
        children: ".attrs"
      }), ":"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-react",
        children: "const Input = styled.input.attrs<{ $size?: string }>((props) => ({\n\ttype: 'text',\n\t$size: props.$size || '1em',\n}))`\n\tborder: 2px solid #bf4f74;\n\tmargin: ${(props) => props.$size};\n\tpadding: ${(props) => props.$size};\n`;\n\n// Input's attrs will be applied first, and then this attrs obj\nconst PasswordInput = styled(Input).attrs({\n  type: \"password\",\n})`\n  // similarly, border will override Input's border\n  border: 2px solid aqua;\n`;\n\nrender(\n  <div>\n    <Input placeholder=\"A bigger text input\" $size=\"2em\" />\n    <br />\n    {/* Notice we can still use the size attr from Input */}\n    <PasswordInput placeholder=\"A bigger password input\" $size=\"2em\" />\n  </div>\n);\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["这就是为什么 ", _jsx(_components.code, {
        children: "PasswordInput"
      }), " 是 ", _jsx(_components.code, {
        children: "'password'"
      }), " 类型，但仍然使用 ", _jsx(_components.code, {
        children: "Input"
      }), " 中的 ", _jsx(_components.code, {
        children: "$size"
      }), " 属性。"]
    }), "\n", _jsxs(_components.p, {
      children: ["¥This is why ", _jsx(_components.code, {
        children: "PasswordInput"
      }), " is of a ", _jsx(_components.code, {
        children: "'password'"
      }), " type, but still uses the ", _jsx(_components.code, {
        children: "$size"
      }), " attribute from ", _jsx(_components.code, {
        children: "Input"
      }), "."]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
